import { MessageOption } from "@/types/conversationTypes";
import { Plane } from "lucide-react";
import { getAirlineLogoUrl } from "@/lib/airlines";
import { useState } from "react";
import FlightOptionTooltip from "./FlightOptionTooltip";

interface Props { option: MessageOption }

const FlightLeg = ({
  departureTime, departureDate, arrivalTime, arrivalDate,
  stops, stopsDetail, duration, originAirport, destinationAirport,
  flightDetails,
}: {
  departureTime: string; departureDate: string;
  arrivalTime: string; arrivalDate: string;
  stops: number; stopsDetail: any[]; duration: string;
  originAirport: string; destinationAirport: string;
  flightDetails: any;
}) => {
  const [activeTooltip, setActiveTooltip] = useState(false);
  const hasStops = (stops || 0) > 0;
  const changeTooltipState = (state: boolean) => () => setActiveTooltip(state);

  return (
    <div className="flex items-center relative">
      <div className="flex flex-col items-start w-24">
        <span className="text-sm font-semibold text-gray-900 leading-tight">{departureTime}</span>
        <span className="text-[11px] text-gray-500 leading-tight">{departureDate}</span>
        <span className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-400">Depart</span>
      </div>
      <div className="flex-1 px-3">
        <div
          className={`relative h-5 flex items-center ${hasStops ? 'cursor-pointer' : 'cursor-default'} focus:outline-none`}
          tabIndex={hasStops ? 0 : -1}
          {...(hasStops ? {
            onMouseEnter: changeTooltipState(true),
            onMouseLeave: changeTooltipState(false),
            onFocus: changeTooltipState(true),
            onBlur: changeTooltipState(false),
            'aria-label': 'Flight timeline hover for details'
          } : {})}
        >
          <div className="absolute left-0 right-0 h-px bg-gray-300" />
          <div className="relative flex justify-between w-full items-center">
            {Array.from({ length: (stops || 0) + 2 }).map((_, idx, arr) => {
              const isEndpoint = idx === 0 || idx === arr.length - 1;
              return <span key={idx} className={isEndpoint ? 'w-2 h-2 bg-brand-500 rounded-full' : 'w-2 h-2 bg-gray-300 rounded-full'} />;
            })}
            <span className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
              <Plane className="w-4 h-4 text-gray-400" />
            </span>
          </div>
        </div>
        <div className="mt-1 flex justify-center">
          {stops > 0 ? (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 border border-orange-100">
              {stops} stop{stops > 1 ? 's' : ''}
            </span>
          ) : (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-100">Direct</span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end w-24 text-right">
        <span className="text-sm font-semibold text-gray-900 leading-tight">{arrivalTime}</span>
        <span className="text-[11px] text-gray-500 leading-tight">{arrivalDate}</span>
        <span className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-400">Arrive</span>
      </div>
      {hasStops && activeTooltip && (
        <div className="absolute inset-x-0 top-2 flex justify-center pointer-events-none z-10">
          <FlightOptionTooltip flightDetails={flightDetails} />
        </div>
      )}
    </div>
  );
};

const FlightOption = ({ option }: Props) => {
  const fd = option.flightDetails!;
  const isRoundTrip = fd.return_departure_date != null;

  // Build a synthetic FlightDetails-compatible object for the return leg tooltip
  const returnLegForTooltip = isRoundTrip ? {
    ...fd,
    departure_time: fd.return_departure_time!,
    departure_date: fd.return_departure_date!,
    arrival_time: fd.return_arrival_time!,
    arrival_date: fd.return_arrival_date!,
    duration: fd.return_duration!,
    stops: fd.return_stops!,
    stops_detail: fd.return_stops_detail!,
    origin_airport: fd.return_origin_airport!,
    destination_airport: fd.return_destination_airport!,
    total_layover_minutes: fd.return_total_layover_minutes!,
  } : null;

  return (
    <div className="p-4 border border-brand-100 rounded-xl bg-white shadow-sm transition ring-1 ring-transparent relative">
      {/* Airline & price row */}
      <div className="flex items-start justify-between mb-3 items-center">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex -space-x-2">
            {fd.airlines.map((iata, idx) => (
              <div key={iata+idx} className="relative inline-flex items-center justify-center">
                <img
                  src={getAirlineLogoUrl(iata)}
                  alt={`Airline ${iata} logo`}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = '/airlines/placeholder.svg'; }}
                  className="w-6 h-6 object-contain"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="text-right flex flex-col items-end">
          <span className="text-base font-bold text-brand-600 tracking-tight">{fd.price}</span>
        </div>
      </div>

      {/* Outbound leg */}
      {isRoundTrip && (
        <div className="flex items-center gap-1 mb-1">
          <Plane className="w-3 h-3 text-brand-400" />
          <span className="text-[10px] uppercase tracking-wide text-brand-500 font-semibold">Outbound</span>
        </div>
      )}
      <FlightLeg
        departureTime={fd.departure_time}
        departureDate={fd.departure_date}
        arrivalTime={fd.arrival_time}
        arrivalDate={fd.arrival_date}
        stops={fd.stops}
        stopsDetail={fd.stops_detail}
        duration={fd.duration}
        originAirport={fd.origin_airport}
        destinationAirport={fd.destination_airport}
        flightDetails={fd}
      />

      {/* Return leg */}
      {isRoundTrip && returnLegForTooltip && (
        <>
          <div className="flex items-center gap-1 mt-3 mb-1">
            <Plane className="w-3 h-3 text-brand-400 rotate-180" />
            <span className="text-[10px] uppercase tracking-wide text-brand-500 font-semibold">Return</span>
          </div>
          <FlightLeg
            departureTime={fd.return_departure_time!}
            departureDate={fd.return_departure_date!}
            arrivalTime={fd.return_arrival_time!}
            arrivalDate={fd.return_arrival_date!}
            stops={fd.return_stops!}
            stopsDetail={fd.return_stops_detail!}
            duration={fd.return_duration!}
            originAirport={fd.return_origin_airport!}
            destinationAirport={fd.return_destination_airport!}
            flightDetails={returnLegForTooltip}
          />
        </>
      )}
    </div>
  );
};

export default FlightOption;
